import api from './api';

/**
 * Billing clients are the parties invoices are raised against. Not to be
 * confused with `clientService`, which manages the website's logo carousel.
 *
 * Money fields stay 2-dp strings exactly as the API sends them.
 */

export const normalizeProject = (raw = {}) => ({
  id: raw.id,
  clientId: raw.billing_client_id,
  description: raw.description ?? '',
  designCode: raw.design_code ?? '',
  hsnCode: raw.hsn_code ?? '',
  taxableAmount: raw.taxable_amount ?? '0.00',
  invoiceStatus: raw.invoice_status,
  invoice: raw.invoice
    ? { id: raw.invoice.id, number: raw.invoice.invoice_number, status: raw.invoice.status }
    : null,
});

export const normalizeBillingClient = (raw = {}) => ({
  id: raw.id,
  clientCode: raw.client_code ?? '',
  name: raw.name ?? '',
  gstin: raw.gstin ?? '',
  stateCode: raw.state_code ?? '',
  stateName: raw.state_name ?? '',
  address: raw.address ?? '',
  email: raw.email ?? '',
  phone: raw.phone ?? '',
  contactPerson: raw.contact_person ?? '',
  notes: raw.notes ?? '',
  isActive: Boolean(Number(raw.status ?? 1)),
  pendingProjectsCount: Number(raw.pending_projects_count ?? 0),
  outstandingAmount: raw.outstanding_amount ?? '0.00',
  pendingProjects: (raw.pending_projects ?? []).map(normalizeProject),
  totals: raw.totals
    ? {
        invoiced: raw.totals.invoiced,
        received: raw.totals.received,
        outstanding: raw.totals.outstanding,
        pendingProjectsValue: raw.totals.pending_projects_value,
      }
    : null,
  createdAt: raw.created_at ?? null,
});

const toApi = (c) => ({
  name: c.name,
  client_code: c.clientCode || null,
  gstin: c.gstin || null,
  state_code: c.stateCode || null,
  address: c.address || null,
  email: c.email || null,
  phone: c.phone || null,
  contact_person: c.contactPerson || null,
  notes: c.notes || null,
  status: c.isActive === undefined ? undefined : (c.isActive ? 1 : 0),
});

const projectToApi = (p) => ({
  description: p.description,
  design_code: p.designCode || null,
  hsn_code: p.hsnCode || null,
  taxable_amount: p.taxableAmount,
});

const page = (d) => ({
  items: d.data,
  total: d.total,
  currentPage: d.current_page,
  lastPage: d.last_page,
  perPage: d.per_page,
});

// The state list never changes during a session, so fetch it once.
let statesPromise = null;

export const billingClientService = {
  getAll: async (params = {}) => {
    const { data } = await api.get('/billing/clients', { params });
    const p = page(data.data);
    return { ...p, items: p.items.map(normalizeBillingClient) };
  },

  search: async (q, config = {}) => {
    const { data } = await api.get('/billing/clients/search', { params: { q }, ...config });
    return data.data.map((c) => ({
      id: c.id,
      name: c.name,
      gstin: c.gstin ?? '',
      clientCode: c.client_code ?? '',
      address: c.address ?? '',
      stateCode: c.state_code ?? '',
      stateName: c.state_name ?? '',
      pendingProjectsCount: Number(c.pending_projects_count ?? 0),
    }));
  },

  getById: async (id) => normalizeBillingClient((await api.get(`/billing/clients/${id}`)).data.data),
  create: async (c) => normalizeBillingClient((await api.post('/billing/clients', toApi(c))).data.data),
  update: async (id, c) => normalizeBillingClient((await api.put(`/billing/clients/${id}`, toApi(c))).data.data),
  delete: (id) => api.delete(`/billing/clients/${id}`),
  toggleStatus: async (id) =>
    normalizeBillingClient((await api.patch(`/billing/clients/${id}/toggle-status`)).data.data),
  suggestCode: async (name) =>
    (await api.get('/billing/clients/suggest-code', { params: { name } })).data.data.client_code,

  /** [{ code: "08", name: "Rajasthan" }, …] */
  states: () => {
    if (!statesPromise) {
      statesPromise = api.get('/billing/states').then((res) => res.data.data).catch((error) => {
        statesPromise = null;
        throw error;
      });
    }
    return statesPromise;
  },

  getProjects: async (clientId, params = {}) => {
    const { data } = await api.get(`/billing/clients/${clientId}/projects`, { params });
    const p = page(data.data);
    return { ...p, items: p.items.map(normalizeProject) };
  },
  createProject: async (clientId, p) =>
    normalizeProject((await api.post(`/billing/clients/${clientId}/projects`, projectToApi(p))).data.data),
  updateProject: async (id, p) =>
    normalizeProject((await api.put(`/billing/projects/${id}`, projectToApi(p))).data.data),
  deleteProject: (id) => api.delete(`/billing/projects/${id}`),
  designCodes: async (q = '', config = {}) =>
    (await api.get('/billing/design-codes', { params: { q }, ...config })).data.data,
};
